import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { EmployeeGuard } from './modules/admin/dashboards/seller/employee.guard';
import { AdminGuard } from './core/auth/guards/admin.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/dashboards/project'
    { path: '', pathMatch: 'full', redirectTo: 'dashboards/project' },

    // Redirect signed in user to the '/dashboards/project'
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'dashboards/project',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.module'
                    ).then((m) => m.AuthConfirmationRequiredModule),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.module'
                    ).then((m) => m.AuthForgotPasswordModule),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.module'
                    ).then((m) => m.AuthResetPasswordModule),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.module').then(
                        (m) => m.AuthSignInModule
                    ),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.module').then(
                        (m) => m.AuthSignUpModule
                    ),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.module').then(
                        (m) => m.AuthSignOutModule
                    ),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.module'
                    ).then((m) => m.AuthUnlockSessionModule),
            },
        ],
    },
    {
        path: 'seller',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,

        data: {
            layout: 'centered',
        },
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            // { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
            {
                path: '',
                loadChildren: () =>
                    import(
                        'app/modules/umin/seller-dashboard/seller-dashboard.module'
                    ).then((m) => m.SellerDashboardModule),
            },

            // { path: 'SalesInvoice', loadChildren: () => import('app/modules/admin/sales-invoice/sales-invoice.module').then(m => m.SalesInvoiceModule) },
            // { path: 'SalesReturn', loadChildren: () => import('app/modules/admin/sales-return/sales-return.module').then(m => m.SalesReturnModule) },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard, AdminGuard],
        canActivateChild: [AuthGuard, AdminGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'contact-us',
                loadChildren: () =>
                    import('app/modules/umin/contact-us/contactus.module').then(
                        (m) => m.ContactusModule
                    ),
            },
            {
                path: 'Settings',
                loadChildren: () =>
                    import(
                        'app/modules/umin/settingspage/settingspage.module'
                    ).then((m) => m.SettingsModule),
            },
            // {
            //     path: 'User',
            //     loadChildren: () =>
            //         import('app/modules/umin/user/user.module').then(
            //             (m) => m.UserModule
            //         ),
            // },
            {
                path: 'Users',
                loadChildren: () =>
                    import(
                        'app/modules/umin/users-types/users-types.module'
                    ).then((m) => m.UsersTypesModule),
            },
            {
                path: 'Banner',
                loadChildren: () =>
                    import('app/modules/umin/banner/banner.module').then(
                        (m) => m.BannerModule
                    ),
            },
            {
                path: 'City',
                loadChildren: () =>
                    import('app/modules/umin/city/city.module').then(
                        (m) => m.CityModule
                    ),
            },

            {
                path: 'Artist',
                loadChildren: () =>
                    import('app/modules/umin/artist/artist.module').then(
                        (m) => m.ArtistModule
                    ),
            },
            {
                path: 'PromoCodes',
                loadChildren: () =>
                    import(
                        'app/modules/umin/promocodes/promocodes.module'
                    ).then((m) => m.PromocodesModule),
            },
            {
                path: 'Sponsor',
                loadChildren: () =>
                    import('app/modules/umin/sponsor/sponsor.module').then(
                        (m) => m.SponsorModule
                    ),
            },
            {
                path: 'EventCategory',
                loadChildren: () =>
                    import(
                        'app/modules/umin/event-category/event-category.module'
                    ).then((m) => m.EventCategoryModule),
            },
            {
                path: 'WelcomePage',
                loadChildren: () =>
                    import(
                        'app/modules/umin/welcome-page/welcome-page.module'
                    ).then((m) => m.WelcomePageModule),
            },
            {
                path: 'Chart',
                loadChildren: () =>
                    import('app/modules/umin/chart/chart.module').then(
                        (m) => m.ChartModule
                    ),
            },
            {
                path: 'Event',
                loadChildren: () =>
                    import('app/modules/umin/event/event.module').then(
                        (m) => m.EventModule
                    ),
            },
            {
                path: 'reports',
                loadChildren: () =>
                    import('app/modules/umin/reports/reports.module').then(
                        (m) => m.ReportsModule
                    ),
            },
            {
                path: 'Role',
                loadChildren: () =>
                    import('app/modules/umin/roles/roles.module').then(
                        (m) => m.RolesModule
                    ),
            },
            {
                path: 'localization',
                loadChildren: () =>
                    import(
                        'app/modules/umin/localization/localization.module'
                    ).then((m) => m.LocalizationModule),
            },

            // Dashboards
            {
                path: 'dashboards',
                children: [
                    {
                        path: 'project',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/dashboards/project/project.module'
                            ).then((m) => m.ProjectModule),
                    },
                    // {
                    //     path: 'analytics',
                    //     loadChildren: () =>
                    //         import(
                    //             'app/modules/admin/dashboards/analytics/analytics.module'
                    //         ).then((m) => m.AnalyticsModule),
                    // },
                    // {
                    //     path: 'finance',
                    //     loadChildren: () =>
                    //         import(
                    //             'app/modules/admin/dashboards/finance/finance.module'
                    //         ).then((m) => m.FinanceModule),
                    // },
                    // {
                    //     path: 'crypto',
                    //     loadChildren: () =>
                    //         import(
                    //             'app/modules/admin/dashboards/crypto/crypto.module'
                    //         ).then((m) => m.CryptoModule),
                    // },
                ],
            },

            // 404 & Catch all
            {
                path: '404-not-found',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/error/error-404/error-404.module'
                    ).then((m) => m.Error404Module),
            },
            { path: '**', redirectTo: '404-not-found' },
        ],
    },
    // Admin routes
];
